/**
 * Karma Core - Intelligent Router
 * 
 * Capability-first routing with scoring function
 */

import type { 
  ExecutionStrategy, 
  KarmaCoreRequest, 
  RoutingScore,
  Modality 
} from "./types";
import { 
  PROVIDER_CAPABILITIES, 
  getProviderCapability,
  getProvidersForModality 
} from "./capabilities";

/**
 * Circuit breaker state
 */
interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  isOpen: boolean;
}

const circuitBreakers: Map<string, CircuitBreakerState> = new Map();
const CIRCUIT_BREAKER_THRESHOLD = 3;
const CIRCUIT_BREAKER_RESET_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Check if circuit breaker is open for a provider
 */
export function isCircuitBreakerOpen(provider: string): boolean {
  const state = circuitBreakers.get(provider);
  if (!state) return false;
  
  if (state.isOpen) {
    // Check if enough time has passed to try again
    if (Date.now() - state.lastFailure > CIRCUIT_BREAKER_RESET_MS) {
      circuitBreakers.set(provider, {
        failures: 0,
        lastFailure: 0,
        isOpen: false,
      });
      return false;
    }
    return true;
  }
  
  return false;
}

// Quota/billing errors are deterministic — once a provider reports
// "insufficient_quota" it stays broken until someone adds credits, so
// there's no reason to spend two more full-timeout attempts confirming
// what the error message already told us. Opens the circuit on this
// single failure instead of waiting for CIRCUIT_BREAKER_THRESHOLD.
const PERMANENT_FAILURE_PATTERNS = [
  /insufficient_quota/i,
  /credit_balance_exhausted/i,
  /credit balance is too low/i,
  /no credits remaining/i,
  /quota exceeded/i,
  /exceeded your current quota/i,
  /billing/i,
];

/**
 * Record a failure for circuit breaker. `errorMessage` is optional so
 * existing call sites that only pass a provider name keep working.
 */
export function recordProviderFailure(provider: string, errorMessage?: string): void {
  const state = circuitBreakers.get(provider) || {
    failures: 0,
    lastFailure: 0,
    isOpen: false,
  };

  const isPermanent = !!errorMessage && PERMANENT_FAILURE_PATTERNS.some((p) => p.test(errorMessage));
  state.failures = isPermanent ? CIRCUIT_BREAKER_THRESHOLD : state.failures + 1;
  state.lastFailure = Date.now();

  if (state.failures >= CIRCUIT_BREAKER_THRESHOLD) {
    state.isOpen = true;
  }

  circuitBreakers.set(provider, state);
}

/**
 * Record a success for circuit breaker
 */
export function recordProviderSuccess(provider: string): void {
  const state = circuitBreakers.get(provider);
  if (state) {
    state.failures = Math.max(0, state.failures - 1);
    if (state.failures === 0) {
      state.isOpen = false;
    }
    circuitBreakers.set(provider, state);
  }
}

/**
 * Calculate routing score for a provider
 */
function calculateScore(
  provider: string,
  request: KarmaCoreRequest,
  strategy: ExecutionStrategy
): RoutingScore | null {
  const capability = getProviderCapability(provider);
  if (!capability) return null;
  
  // Check circuit breaker
  if (isCircuitBreakerOpen(provider)) {
    return null;
  }
  
  // Check modality support
  if (request.modality && !capability.modalities.includes(request.modality)) {
    return null;
  }
  
  // Check function calling requirement
  if (request.tools && request.tools.length > 0 && !capability.functionCalling) {
    return null;
  }
  
  // Strategy-specific weights
  let qualityWeight = 0.4;
  let latencyWeight = 0.3;
  let costWeight = 0.2;
  let reliabilityWeight = 0.1;
  
  switch (strategy) {
    case "FAST":
      qualityWeight = 0.2;
      latencyWeight = 0.6;
      costWeight = 0.1;
      reliabilityWeight = 0.1;
      break;
    case "DEEP":
      qualityWeight = 0.6;
      latencyWeight = 0.1;
      costWeight = 0.1;
      reliabilityWeight = 0.2;
      break;
    case "CHEAP":
      qualityWeight = 0.1;
      latencyWeight = 0.2;
      costWeight = 0.6;
      reliabilityWeight = 0.1;
      break;
    case "HIGH_STAKES":
      qualityWeight = 0.5;
      latencyWeight = 0.2;
      costWeight = 0.1;
      reliabilityWeight = 0.2;
      break;
  }
  
  // Normalize scores (0-1 scale)
  // Quality: Based on max context and function calling support
  const quality = Math.min(1, (capability.maxContext / 200000) * 0.5 + (capability.functionCalling ? 0.5 : 0));
  
  // Latency: Lower is better, normalize to 0-1 (inverse)
  const maxLatency = 5000;
  const latency = 1 - Math.min(1, capability.averageLatency / maxLatency);
  
  // Cost: Lower is better, normalize to 0-1 (inverse)
  const avgCost = (capability.costPer1kTokens.input + capability.costPer1kTokens.output) / 2;
  const maxCost = 0.02; // $0.02 per 1k tokens
  const cost = 1 - Math.min(1, avgCost / maxCost);
  
  // Reliability: Already 0-1
  const reliability = capability.reliabilityScore;
  
  // Calculate weighted score
  const score = 
    qualityWeight * quality +
    latencyWeight * latency +
    costWeight * cost +
    reliabilityWeight * reliability;
  
  return {
    provider,
    model: capability.models[0] || "unknown",
    score,
    breakdown: {
      quality,
      latency,
      cost,
      reliability,
    },
  };
}

/**
 * Route request to best provider
 */
export function routeRequest(
  request: KarmaCoreRequest
): RoutingScore[] {
  const strategy: ExecutionStrategy = request.strategy || "NORMAL";
  const modality: Modality = request.modality || "text";
  
  // Get providers that support the modality
  const candidateProviders = getProvidersForModality(modality);
  
  // Calculate scores for all candidates
  const scores: RoutingScore[] = candidateProviders
    .map(provider => calculateScore(provider, request, strategy))
    .filter((score): score is RoutingScore => score !== null)
    .sort((a, b) => b.score - a.score); // Sort by score descending
  
  return scores;
}

/**
 * Get fallback chain (at least 2 alternates)
 */
export function getFallbackChain(
  primaryProvider: string,
  request: KarmaCoreRequest
): string[] {
  const scores = routeRequest(request);
  const providers = scores.map(s => s.provider);
  
  // Remove primary from list
  const fallbacks = providers.filter(p => p !== primaryProvider);
  
  // Return at least 2 fallbacks
  return fallbacks.slice(0, Math.max(2, fallbacks.length));
}

/**
 * Classify task type (cheap heuristic)
 */
export function classifyTask(message: string): {
  needsWebSearch: boolean;
  needsImageGeneration: boolean;
  needsVideoGeneration: boolean;
  needsCodeGeneration: boolean;
  complexity: "simple" | "medium" | "complex";
} {
  const lower = message.toLowerCase();
  
  return {
    needsWebSearch: 
      lower.includes("weather") ||
      lower.includes("current") ||
      lower.includes("latest") ||
      lower.includes("news") ||
      lower.includes("search") ||
      lower.includes("find information"),
    needsImageGeneration:
      lower.includes("generate") && (lower.includes("image") || lower.includes("photo") || lower.includes("picture")) ||
      lower.includes("create") && (lower.includes("image") || lower.includes("photo") || lower.includes("picture")) ||
      lower.includes("draw") ||
      lower.includes("make an image"),
    needsVideoGeneration:
      lower.includes("generate") && lower.includes("video") ||
      lower.includes("create") && lower.includes("video") ||
      lower.includes("make a video"),
    needsCodeGeneration:
      lower.includes("code") ||
      lower.includes("function") ||
      lower.includes("script") ||
      lower.includes("program") ||
      lower.includes("implement"),
    complexity: 
      message.length > 500 ? "complex" :
      message.length > 100 ? "medium" :
      "simple",
  };
}
